"use client"

import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useCookies } from "react-cookie"
import { useModal } from "../modal/Provdier"
import { useVoice } from "@/hooks/useVoice"
import { getTenantId } from "@/hooks/getBaseUrl"
import { apiFetch } from "@/lib/apiFetch"
import { filterKnowledgeByTenant } from "@/constants/knowledge"
import { filterVisibleModels } from "@/constants/model"
import VoiceWave from "./VoiceWave"
import { SOURCE_CHIPS } from "./data"

const KEXIN_CHAT_PATH = "/chat/teacher-chat"
const TEACHER_CHAT_PAYLOAD_COOKIE = "teacherChatPayload"
// TODO(agent-scope): agentId 来源待定, 与 huizhi 模板一致, 临时使用固定 AgentScope 智能体
const TEMP_AGENT_SCOPE_AGENT_ID = "2062789053285715969"
const MAX_UPLOAD_COUNT = 3
const MAX_UPLOAD_SIZE = 30 * 1024 * 1024

interface UploadedFile {
  fileId: string
  fileName: string
  fileType: string
  fileUrl?: string
}

interface ModelCatalogItem {
  agentId?: string
  displayName?: string
  enabled?: boolean
  modelName?: string
  modelProfileId: string
  providerType?: string
}

interface KnowledgeOption {
  displayName: string
  knowledgeKey: string
}

function buildChatUrl(params: Record<string, string>) {
  const url = new URL(KEXIN_CHAT_PATH, window.location.origin)
  Object.entries(params).forEach(([key, value]) => {
    if (value) {
      url.searchParams.set(key, value)
    }
  })
  return url.toString()
}

function getFileType(file: File, uploaded?: Record<string, any>) {
  return (
    uploaded?.fileType?.toString?.() ||
    uploaded?.type?.toString?.() ||
    file.type ||
    file.name.split(".").pop() ||
    "file"
  )
}

function normalizeUploadedFile(file: File, uploaded: Record<string, any>): UploadedFile {
  return {
    fileId: (uploaded?.fileId || uploaded?.id || uploaded?.filePath || uploaded?.fileUrl || "").toString(),
    fileName: uploaded?.fileName || uploaded?.name || file.name,
    fileType: getFileType(file, uploaded),
    fileUrl: uploaded?.fileUrl || uploaded?.url || uploaded?.filePath,
  }
}

function writeTeacherChatPayload(payload: {
  question: string
  model: string
  modelProfileId: string
  kb: string
  activeKnowledgeKeys: string[]
  files: UploadedFile[]
  webSearchEnabled: boolean
}) {
  document.cookie = `${TEACHER_CHAT_PAYLOAD_COOKIE}=${encodeURIComponent(
    JSON.stringify(payload)
  )}; path=/; max-age=300; SameSite=Lax`
}

const pillClass =
  "inline-flex h-10 items-center gap-1 rounded-full bg-white px-3 text-base text-[#131921] shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.1)]"
const roundIconClass =
  "inline-flex size-10 items-center justify-center rounded-full bg-white text-[#131921] shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.1)] disabled:cursor-not-allowed disabled:opacity-50"

export default function SearchPanel({
  isExpired,
  query,
  onQueryChange,
}: {
  isExpired: boolean
  query: string
  onQueryChange: (value: string) => void
}) {
  const { openModal, setIsLoginOpen } = useModal()
  const [cookies] = useCookies(["token"])
  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [mounted, setMounted] = useState(false)

  // 语音识别完成后, 将文本追加到输入框
  const handleVoiceDone = useCallback(
    (text: string) => {
      onQueryChange(query ? `${query}${text}` : text)
    },
    [query, onQueryChange]
  )
  const { status: voiceStatus, toggleRecord } = useVoice(handleVoiceDone)

  const [model, setModel] = useState("")
  const [kb, setKb] = useState("")
  const [webSearchEnabled, setWebSearchEnabled] = useState(false)
  const [modelOptions, setModelOptions] = useState<ModelCatalogItem[]>([])
  const [knowledgeOptions, setKnowledgeOptions] = useState<KnowledgeOption[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([])
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState("")
  const tokenHead = cookies.token?.tokenHead || ""
  const tokenValue = cookies.token?.token || ""
  const authorization = useMemo(() => {
    return tokenValue ? `${tokenHead}${tokenValue}` : ""
  }, [tokenHead, tokenValue])
  const selectedAgentId = useMemo(() => {
    const selectedModel = modelOptions.find((item) => String(item.modelProfileId) === String(model))
    return selectedModel?.agentId || TEMP_AGENT_SCOPE_AGENT_ID
  }, [model, modelOptions])

  useEffect(() => {
    setMounted(true)
  }, [])

  // 点击语音按钮: 未登录弹出登录框, 已登录开始/结束录音
  function handleVoiceClick() {
    if (!cookies.token) {
      setIsLoginOpen(true)
      return
    }
    toggleRecord()
  }

  useEffect(() => {
    let alive = true
    async function loadModelCatalog() {
      try {
        const response = await apiFetch("/agentScope/chat/models", { authorization })
        const result = await response.json()
        if (!response.ok || result.code !== 0) {
          if (alive) setModelOptions([])
          return
        }
        const options = Array.isArray(result.data?.items)
          ? filterVisibleModels<ModelCatalogItem>(
            result.data.items.filter(
              (item: ModelCatalogItem) => item?.modelProfileId !== undefined && item?.enabled !== false,
            ),
          )
          : []
        if (!alive) return
        setModelOptions(options)
        if (options[0]?.modelProfileId !== undefined) {
          setModel((current) => current || String(options[0].modelProfileId))
        }
      } catch {
        if (alive) setModelOptions([])
      }
    }

    loadModelCatalog()

    return () => {
      alive = false
    }
  }, [authorization])

  useEffect(() => {
    let alive = true
    async function loadAgentRuntime() {
      try {
        const params = new URLSearchParams({ agentId: selectedAgentId })
        const response = await apiFetch(`/agentScope/chat/agent/runtime?${params.toString()}`, { authorization })
        const result = await response.json()
        if (!response.ok || result.code !== 0) return
        const availableItems: KnowledgeOption[] = Array.isArray(result.data?.knowledge)
          ? result.data.knowledge
            .filter(
              (item: any) => item?.available !== false && item?.enabled !== false && item?.catalog?.enabled !== false,
            )
            .map((item: any) => ({
              displayName:
                item?.catalog?.displayName || item?.knowledgeKey || item?.catalog?.knowledgeKey || "知识库",
              knowledgeKey: item?.knowledgeKey || item?.catalog?.knowledgeKey || "",
            }))
            .filter((item: KnowledgeOption) => item.knowledgeKey)
          : []
        // 按机构白名单展示知识库, 默认选中第一个
        const items = filterKnowledgeByTenant(availableItems, getTenantId())
        if (!alive) return
        setKnowledgeOptions(items)
        setKb((current) =>
          items.some((item) => item.knowledgeKey === current) ? current : items[0]?.knowledgeKey || "",
        )
      } catch {
        if (alive) setKnowledgeOptions([])
      }
    }

    loadAgentRuntime()

    return () => {
      alive = false
    }
  }, [authorization, selectedAgentId])

  async function uploadFile(file: File) {
    const formData = new FormData()
    formData.append("file", file)
    formData.append("fileType", "7")
    formData.append("fileFolderType", "8")

    const response = await apiFetch("/aiChat/chatUploadFile", {
      method: "POST",
      authorization,
      body: formData,
    })
    const result = await response.json()
    if (!response.ok || result.code !== 0 || !result.data) {
      throw new Error(result.msg || result.message || "上传失败")
    }
    return normalizeUploadedFile(file, result.data)
  }

  async function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    const selectedFiles = Array.from(event.target.files || [])
    event.target.value = ""
    if (!selectedFiles.length) return

    setUploadError("")
    if (!cookies.token) {
      setIsLoginOpen(true)
      return
    }

    if (uploadedFiles.length + selectedFiles.length > MAX_UPLOAD_COUNT) {
      setUploadError(`最多上传 ${MAX_UPLOAD_COUNT} 个文件`)
      return
    }

    const oversizedFile = selectedFiles.find((file) => file.size > MAX_UPLOAD_SIZE)
    if (oversizedFile) {
      setUploadError(`${oversizedFile.name} 超过 30MB`)
      return
    }

    setUploading(true)
    try {
      const files = await Promise.all(selectedFiles.map(uploadFile))
      setUploadedFiles((current) => [...current, ...files])
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "上传失败")
    } finally {
      setUploading(false)
    }
  }

  function handleSearch() {
    if (!query.trim() && !uploadedFiles.length) return
    if (!cookies.token) {
      setIsLoginOpen(true)
      return
    }
    if (isExpired) {
      openModal()
      return
    }
    writeTeacherChatPayload({
      question: query.trim(),
      model,
      modelProfileId: model,
      kb,
      activeKnowledgeKeys: kb ? [kb] : [],
      files: uploadedFiles,
      webSearchEnabled,
    })
    window.open(
      buildChatUrl({
        q: query.trim(),
        modelProfileId: model,
        knowledgeKey: kb,
        webSearchEnabled: String(webSearchEnabled),
      }),
      "_blank"
    )
  }

  const heroItemClass = (delay: string) =>
    `inline-block transition-all duration-700 ease-out ${delay} ${mounted ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`

  return (
    <section id="kexin-search" className="relative z-10 flex w-full max-w-[960px] flex-col items-center gap-5">
      <div className="flex w-full flex-col items-center gap-2.5 text-center">
        <h1 className="w-full text-[42px] font-bold leading-[50px] text-[#131921] md:text-[64px] md:leading-[72px]">
          <span className={heroItemClass("delay-100")}>每一个答案，</span>
          <span
            className={`bg-gradient-to-r from-[#5384ff] to-[#3c10ff] bg-clip-text text-transparent ${heroItemClass("delay-200")}`}
          >
            都有来源
          </span>
        </h1>
        <p className={`w-full text-2xl leading-8 text-[#303133] md:text-[32px] md:leading-10 ${heroItemClass("delay-300")}`}>
          今天想验证什么问题？
        </p>
      </div>

      <form
        className="flex min-h-[132px] w-full flex-col rounded-[18px] border border-[#e8eaee] bg-white p-[17px] shadow-[0_2px_3px_rgba(0,0,0,0.1)]"
        onSubmit={(event) => {
          event.preventDefault()
          handleSearch()
        }}
        aria-label="可信检索"
      >
        <textarea
          id="question-input"
          name="question"
          value={query}
          onChange={(event) => onQueryChange(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" && !event.shiftKey) {
              event.preventDefault()
              handleSearch()
            }
          }}
          rows={2}
          placeholder="输入你想验证的问题，例如：这项政策的影响，有哪些公开资料可以支持？"
          className="min-h-[56px] w-full resize-none bg-transparent text-base leading-7 text-[#131921] placeholder:text-[#909399] focus:outline-none"
        />

        <div className="flex w-full flex-wrap items-center justify-between gap-2.5 pt-3.5">
          <div className="flex flex-wrap items-center gap-2.5">
            <label className={`${pillClass} cursor-pointer pl-2`}>
              <i className="font_family icon-Knowledge text-2xl leading-none" aria-hidden="true" />
              <select
                name="knowledgeKey"
                value={kb}
                onChange={(event) => setKb(event.target.value)}
                className="max-w-[140px] cursor-pointer bg-transparent text-sm focus:outline-none"
                aria-label="选择知识库"
              >
                <option value="">不参考知识库</option>
                {knowledgeOptions.map((item) => (
                  <option key={item.knowledgeKey} value={item.knowledgeKey}>
                    {item.displayName}
                  </option>
                ))}
              </select>
            </label>

            <label className={`${pillClass} cursor-pointer pl-2`}>
              <i className="font_family icon-AI text-2xl leading-none" aria-hidden="true" />
              <select
                name="modelProfileId"
                value={model}
                onChange={(event) => setModel(event.target.value)}
                className="max-w-[140px] cursor-pointer bg-transparent text-sm focus:outline-none"
                aria-label="选择模型"
              >
                {modelOptions.length ? (
                  modelOptions.map((item) => (
                    <option key={item.modelProfileId} value={String(item.modelProfileId)}>
                      {item.displayName || item.modelName || item.providerType || item.modelProfileId}
                    </option>
                  ))
                ) : (
                  <option value="">模型加载中</option>
                )}
              </select>
            </label>

            <button
              type="button"
              onClick={() => setWebSearchEnabled((enabled) => !enabled)}
              aria-pressed={webSearchEnabled}
              className={`inline-flex h-10 items-center gap-1 rounded-full bg-white px-3 pl-2 text-sm text-[#131921] transition-all hover:-translate-y-px ${
                webSearchEnabled
                  ? "border border-[#131921]"
                  : "border border-transparent shadow-[0_2px_3px_rgba(0,0,0,0.1)] hover:shadow-[0_4px_10px_rgba(19,25,33,0.1)]"
              }`}
            >
              <i className="font_family icon-Area text-2xl leading-none" aria-hidden="true" />
              <span className="hidden sm:inline">联网搜索</span>
            </button>
          </div>

          <div className="flex shrink-0 items-center gap-2.5">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={handleFileChange}
              accept=".pdf,.xlsx,.xls,.doc,.docx,.txt,.png,.jpeg,.jpg,.ppt,.pptx"
            />
            <button
              type="button"
              onClick={() => {
                if (!cookies.token) {
                  setIsLoginOpen(true)
                  return
                }
                fileInputRef.current?.click()
              }}
              disabled={uploading || uploadedFiles.length >= MAX_UPLOAD_COUNT}
              className={roundIconClass}
              aria-label="上传文档"
            >
              <i className="font_family icon-Upload text-2xl leading-none" aria-hidden="true" />
            </button>

            <button
              type="button"
              onClick={handleVoiceClick}
              className={
                voiceStatus === "idle"
                  ? roundIconClass
                  : "inline-flex size-10 items-center justify-center rounded-full bg-[#131921] text-white shadow-[0_4px_10px_rgba(19,25,33,0.3)] transition-all"
              }
              aria-label={voiceStatus === "recording" ? "结束录音" : "语音输入"}
            >
              {voiceStatus === "loading" ? (
                <span className="size-5 animate-spin rounded-full border-2 border-white/30 border-t-white" aria-hidden="true" />
              ) : voiceStatus === "recording" ? (
                <VoiceWave className="text-white" />
              ) : (
                <i className="font_family icon-Audio text-2xl leading-none" aria-hidden="true" />
              )}
            </button>

            <button
              type="submit"
              className={`inline-flex size-10 items-center justify-center rounded-full bg-[#131921] text-white shadow-[0_2px_3px_rgba(0,0,0,0.1)] transition-all hover:-translate-y-px hover:shadow-[0_4px_10px_rgba(19,25,33,0.2)] ${query.trim() || uploadedFiles.length ? "" : "opacity-40"}`}
              aria-label="发送问题"
            >
              <i className="font_family icon-Arrow_Forward-2 text-2xl leading-none" aria-hidden="true" />
            </button>
          </div>
        </div>
      </form>

      {(uploadedFiles.length > 0 || uploadError || uploading) && (
        <div className="flex w-full flex-wrap items-center gap-2 text-sm">
          {uploadedFiles.map((file, index) => (
            <div
              key={`${file.fileId}-${index}`}
              className="flex max-w-full items-center gap-2 rounded-lg border border-[#e4e7ed] bg-white px-3 py-1.5 text-[#606266]"
            >
              <span className="truncate">{file.fileName}</span>
              <button
                type="button"
                onClick={() =>
                  setUploadedFiles((current) => current.filter((_, itemIndex) => itemIndex !== index))
                }
                className="text-[#909399] transition-colors hover:text-red-500"
                aria-label={`移除 ${file.fileName}`}
              >
                <i className="font_family icon-Close text-2xl leading-none" aria-hidden="true" />
              </button>
            </div>
          ))}
          {uploading && <span className="text-[#909399]">上传中...</span>}
          {uploadError && <span className="text-red-500">{uploadError}</span>}
        </div>
      )}

      <div
        className="flex w-full justify-center gap-2.5 overflow-x-auto md:gap-3 [&::-webkit-scrollbar]:hidden"
        aria-label="可信来源类型"
      >
        {SOURCE_CHIPS.map((chip) => (
          <span
            key={chip}
            className="inline-flex min-h-[34px] items-center whitespace-nowrap rounded-lg border border-[#e4e7ed] bg-white/80 px-4 text-base text-[#131921]"
          >
            {chip}
          </span>
        ))}
      </div>
    </section>
  )
}
